/**
 * Chart color utilities to provide consistent coloring across data visualizations
 */

// Color palette based on the project's Tailwind theme
export const colorMap: Record<string, Record<number, string>> = {
  blue: {
    300: '#CAD1F3', // blue-300
    400: '#A8B6ED', // blue-400
    500: '#7F96E7', // blue-500
    600: '#3F6DE1', // blue-600
    700: '#3861C9', // blue-700
    800: '#3154AE', // blue-800
    900: '#28458E', // blue-900
  },
  lightblue: {
    300: '#C6D7EB', // lightblue-300
    400: '#A1C0E0', // lightblue-400
    500: '#72A5D5', // lightblue-500
    600: '#0086C9', // lightblue-600
    700: '#0078B4', // lightblue-700
    800: '#00689C', // lightblue-800
    900: '#00557F', // lightblue-900
  },
  purple: {
    300: '#D8C6FD', // purple-300
    400: '#C2A1FB', // purple-400
    500: '#A972FA', // purple-500
    600: '#8C00F9', // purple-600
    700: '#7D00DF', // purple-700
    800: '#6C00C1', // purple-800
    900: '#59009D', // purple-900
  },
  successful: {
    300: '#C6DBCA', // successful-300
    400: '#A1C6AA', // successful-400
    500: '#72AF82', // successful-500
    600: '#079445', // successful-600
    700: '#06843E', // successful-700
    800: '#057335', // successful-800
    900: '#045E2C', // successful-900
  },
  warn: {
    300: '#FDE8C6', // warn-300
    400: '#FDDBA1', // warn-400
    500: '#FCCE72', // warn-500
    600: '#FBC000', // warn-600
    700: '#E1AC00', // warn-700
    800: '#C29500', // warn-800
    900: '#9F7900', // warn-900
  },
  phaserbeam: {
    300: '#FFCBC6', // phaserbeam-300
    400: '#FFACA1', // phaserbeam-400
    500: '#FF8572', // phaserbeam-500
    600: '#FF4D00', // phaserbeam-600
    700: '#E44500', // phaserbeam-700
    800: '#C63C00', // phaserbeam-800
    900: '#A13100', // phaserbeam-900
  },
  error: {
    300: '#F9CACA', // error-300
    400: '#F6A9AA', // error-400
    500: '#F38181', // error-500
    600: '#F04344', // error-600
    700: '#D73C3D', // error-700
    800: '#BA3435', // error-800
    900: '#982A2B', // error-900
  },
  gray: {
    300: '#D1D1D8', // gray-300
    400: '#B6B6C1', // gray-400
    500: '#9595A7', // gray-500
    600: '#6C6C89', // gray-600
    700: '#61617B', // gray-700
    800: '#54546A', // gray-800
    900: '#444457', // gray-900
  },
};

/**
 * Calculate color intensity indexes centered around 600 for a given number of items
 *
 * Examples:
 * - 1 item: [600]
 * - 2 items: [500, 700]
 * - 3 items: [500, 600, 700]
 * - 4 items: [400, 500, 700, 800]
 * - 5 items: [400, 500, 600, 700, 800]
 * - 6 items: [300, 400, 500, 700, 800, 900]
 * - 7 items: [300, 400, 500, 600, 700, 800, 900]
 *
 * @param count Number of items to generate colors for
 * @returns Array of color intensity indexes
 */
export const calculateCenteredColorIndexes = (count: number): number[] => {
  const result: number[] = [];
  const middle = 600; // The middle intensity we want to use
  const step = 100; // The step between color intensities

  if (count === 1) {
    return [middle];
  }

  if (count % 2 === 0) {
    // Even number of items
    const halfCount = count / 2;
    for (let i = 0; i < count; i++) {
      if (i < halfCount) {
        result.push(middle - (halfCount - i) * step);
      } else {
        result.push(middle + (i - halfCount + 1) * step);
      }
    }
  } else {
    // Odd number of items
    const midIndex = Math.floor(count / 2);
    for (let i = 0; i < count; i++) {
      if (i < midIndex) {
        result.push(middle - (midIndex - i) * step);
      } else if (i > midIndex) {
        result.push(middle + (i - midIndex) * step);
      } else {
        result.push(middle);
      }
    }
  }

  // Ensure color indexes are within the valid range (300-900)
  return result.map((index) => Math.max(300, Math.min(900, index)));
};

/**
 * Get a color hex value for an item based on its color family and color index
 *
 * @param colorFamily The color family name (e.g., 'blue', 'purple')
 * @param colorIndex The color intensity index (e.g., 600, 700)
 * @returns Hex color string
 */
export const getColorByFamilyAndIndex = (
  colorFamily: string,
  colorIndex: number
): string => {
  // Return the color or a default if not found
  return colorMap[colorFamily]?.[colorIndex] || colorMap.gray[600]; // default to gray-600
};

/**
 * Get chart colors for a group of items with the same color family
 *
 * @param items Array of items to color
 * @param colorFamily Color family to use
 * @returns Array of hex color values
 */
export const getChartColorsForGroup = <T>(items: T[], colorFamily: string): string[] => {
  if (items.length === 0) return [];

  const colorIndexes = calculateCenteredColorIndexes(items.length);
  return colorIndexes.map((index) => getColorByFamilyAndIndex(colorFamily, index));
};

/**
 * Apply color properties to items based on their category
 *
 * @param items Array of items to colorize
 * @param getColorFamily Function that determines the color family for each item
 * @returns Array of items with colorFamily and colorIndex properties
 */
export const applyColorPropertiesToItems = <T extends Record<string, unknown>>(
  items: T[],
  getColorFamily: (item: T) => string
): (T & { colorFamily: string; colorIndex: number })[] => {
  // Group items by color family
  const itemsByFamily: Record<string, T[]> = {};

  items.forEach((item) => {
    const family = getColorFamily(item);
    if (!itemsByFamily[family]) {
      itemsByFamily[family] = [];
    }
    itemsByFamily[family].push(item);
  });

  // Apply color indexes to each group and flatten
  const result: (T & { colorFamily: string; colorIndex: number })[] = [];

  Object.entries(itemsByFamily).forEach(([family, familyItems]) => {
    const colorIndexes = calculateCenteredColorIndexes(familyItems.length);

    familyItems.forEach((item, index) => {
      result.push({
        ...item,
        colorFamily: family,
        colorIndex: colorIndexes[index],
      });
    });
  });

  return result;
};
